/**
 * Shared client-side game interfaces. Every gameplay module (vehicles, track,
 * physics, ui, hud) imports its types from here so the modules can be developed
 * independently without interface drift. The orchestrator (main.ts) wires the
 * concrete implementations together.
 *
 * Coordinate conventions (IMPORTANT — every module must agree):
 *   - World is Y-up. The track lies on the y = 0 plane.
 *   - A kart's `heading` is a yaw angle in radians. Its forward direction is
 *         forward = new THREE.Vector3(Math.sin(heading), 0, Math.cos(heading))
 *     i.e. heading = 0 points toward +Z. Rotation is standard right-handed
 *     (positive heading turns from +Z toward +X... i.e. clockwise viewed from
 *     above with Y up). Vehicle meshes are modelled facing +Z.
 */
import * as THREE from 'three';

import type { CheckpointState } from './progress';

// ---------------------------------------------------------------------------
// Vehicles
// ---------------------------------------------------------------------------
export interface VehicleStats {
  id: string;
  name: string;
  emoji: string;
  description: string;
  /** Primary body color (used for HUD swatches and fallbacks). */
  color: number;
  /** Top forward speed in world units / second. */
  maxSpeed: number;
  /** Forward acceleration in world units / second^2. */
  accel: number;
  /** Top reverse speed (positive number) in world units / second. */
  reverseSpeed: number;
  /** Base steering rate in radians / second at cruising speed. */
  handling: number;
  /** Lateral grip 0..1 (1 = no sliding, lower = more drift). */
  grip: number;
  /** Multiplier applied to top speed & accel while boosting (e.g. 1.6). */
  boostMul: number;
}

/**
 * A built kart: the renderable group plus the handful of sub-objects and local
 * anchor points the game animates every frame (wheel spin, steering, exhaust
 * flames, tyre smoke). Local space is the modelling space — kart faces +Z,
 * wheels resting on y = 0.
 */
export interface VehicleVisual {
  /** Add this to the scene; positioned/rotated from the kart state. */
  group: THREE.Group;
  /** Every wheel — spun about its own X axis in proportion to ground speed. */
  wheels: THREE.Object3D[];
  /** Subset of `wheels` that also yaws with the steering input. */
  steered: THREE.Object3D[];
  /** Local-space points where boost flames / exhaust puffs emit. */
  exhausts: THREE.Vector3[];
  /** Local-space rear contact patches: tyre smoke and skid marks spawn here. */
  contacts: THREE.Vector3[];
  /** Materials that flare up while the boost is firing. */
  glow: THREE.MeshStandardMaterial[];
  /** Materials that light up while the kart is scrubbing off speed. */
  brakeLights: THREE.MeshStandardMaterial[];
}

// ---------------------------------------------------------------------------
// Track
// ---------------------------------------------------------------------------
export interface Checkpoint {
  /** Order around the loop. Index 0 is the start/finish line. */
  index: number;
  /** Gate center on the y = 0 plane. */
  position: THREE.Vector3;
  /**
   * Half the gate's width. A gate is a line segment across the track, not a
   * circle around the centerline, and this must span the whole drivable
   * corridor out to the barriers — otherwise a kart running wide on the runoff
   * slips past without registering, and its lap never completes.
   */
  halfWidth: number;
  /** Unit direction of travel through the gate. */
  forward: THREE.Vector3;
}

/**
 * The renderable half of the checkpoints: a lit gate straddling the road at
 * every `Checkpoint`, driven by the same state the HUD shows. Without these the
 * gates are invisible geometry, and a driver has no way to see what they just
 * crossed — or what they just drove around.
 */
export interface CheckpointMarkers {
  /** Renderable gates. Parented to the track group, so it moves with the map. */
  group: THREE.Group;
  /** Apply one state per checkpoint index (same order as `checkpoints`). */
  setStates(states: readonly CheckpointState[]): void;
  /** Flare one gate up — the kart just went through it. */
  pulse(index: number): void;
  /** Advance the idle pulse and decay any flares. */
  update(dt: number): void;
}

/** Result of querying the track about a world position. */
export interface RoadSample {
  /** Nearest point on the road centerline (y = 0). */
  point: THREE.Vector3;
  /** Unit track direction at that point. */
  forward: THREE.Vector3;
  /** Lateral distance from the centerline. */
  distance: number;
  /** True when `distance <= roadWidth / 2`. */
  onRoad: boolean;
}

export interface StartSlot {
  position: THREE.Vector3;
  heading: number;
}

/**
 * Everything outside the track mesh that sets a map's mood: sky gradient, sun,
 * fog and the tone-mapping/bloom the renderer should use. main.ts applies this
 * whenever the track changes, so each map genuinely looks like its own place.
 */
export interface TrackEnvironment {
  /** Zenith colour of the sky dome. */
  skyTop: number;
  /** Horizon colour of the sky dome (fog blends toward this). */
  skyHorizon: number;
  /** Ground haze under the horizon. */
  skyGround: number;
  /** Direction the sun sits in, relative to the kart (any length). */
  sunDirection: THREE.Vector3;
  sunColor: number;
  sunIntensity: number;
  /** Hemisphere fill: sky-side and bounce-from-the-ground colours. */
  hemiSky: number;
  hemiGround: number;
  hemiIntensity: number;
  ambientIntensity: number;
  fogColor: number;
  /** Exponential-squared fog density — small numbers (~0.0015). */
  fogDensity: number;
  /** Tone-mapping exposure for this mood. */
  exposure: number;
  /** Bloom strength; 0 disables the pass for this map. */
  bloom: number;
  /** Colour of the dust plume kicked up when a kart leaves the road. */
  dustColor: number;
  /** Whether the sun should be drawn as a visible disc + glow. */
  sunVisible: boolean;
}

export interface TrackData {
  /** Renderable track geometry (road, walls, scenery). Add to the scene. */
  group: THREE.Group;
  /** Ordered checkpoint loop; index 0 is the start/finish line. */
  checkpoints: Checkpoint[];
  /** Visible gates for those checkpoints, already inside `group`. */
  checkpointMarkers: CheckpointMarkers;
  /** Grid slots, ordered front-to-back. At least MAX_PLAYERS of them. */
  startPositions: StartSlot[];
  /** Full road width in world units. */
  roadWidth: number;
  /**
   * Distance from the centerline at which the guardrail stands. Karts are
   * blocked here, so the runoff between the kerb and the barrier is drivable
   * (and slow) but the scenery is not.
   */
  wallRadius: number;
  /** Ordered centerline sample points around the loop (used by the minimap). */
  centerline: THREE.Vector3[];
  /** Sky / light / fog / post settings for this map. */
  environment: TrackEnvironment;
  /** Query nearest-centerline info for off-road detection & respawn. */
  sample(pos: THREE.Vector3): RoadSample;
}

// ---------------------------------------------------------------------------
// Physics
// ---------------------------------------------------------------------------
export interface KartInput {
  /**
   * -1 .. 1. Pushing against the direction of travel brakes hard first and only
   * engages reverse once the kart has actually stopped — so there is no separate
   * brake input.
   */
  throttle: number;
  /** -1 (left) .. 1 (right). */
  steer: number;
  boost: boolean;
  /** Handbrake drift: sheds grip to slide through corners (not a brake). */
  drift: boolean;
}

export interface KartState {
  position: THREE.Vector3;
  /** World-space velocity. */
  velocity: THREE.Vector3;
  /** Yaw in radians (see coordinate conventions above). */
  heading: number;
  /** Signed forward speed (positive = forward). */
  speed: number;
  /** Available boost, 0..1. */
  boostMeter: number;
  /** True on frames where boost is actively applied. */
  boosting: boolean;
  /** True while the kart is scrubbing speed against its direction of travel. */
  braking: boolean;
  /** Sideways slip in units/second — drives tyre smoke, skid marks and screech. */
  slip: number;
  /** How far off the road the kart is, 0 (on tarmac) .. 1 (deep in the dirt). */
  offRoad: number;
  /** Full render orientation including visual lean/tilt. */
  bodyQuat: THREE.Quaternion;
}

// A neutral, all-zero input any module can reuse.
export const NO_INPUT: KartInput = { throttle: 0, steer: 0, boost: false, drift: false };
